// Settings → Users (§13.10 — real implementation).
// Spec: docs/spec/core/part2-product-requirements.md §13.10 (Reqs 654-660).
// Spec: docs/spec/core/part3-ux-spec.md §9.7.
// Mockup: docs/spec/topics/onboarding/mockups/cade-onboarding-mockups.html
//         (#section-users-settings).
//
// All three tabs are fully built: Active (Req 655), Pending invitations
// (Reqs 656-659), and Deactivated (mockup §X.7).

import { useEffect, useMemo, useRef, useState } from 'react';
import { trpc } from '../../api/trpc';
import { useAuth } from '../../hooks/useAuth';

type TabId = 'active' | 'pending' | 'deactivated';

// Offline/error fallback set for the invite modal's personal note.
// Per Req 657 the live note is LLM-generated fresh on open and on every
// "Suggest another" (via trpc.aiOrchestrator.suggestInviteNote). These seven
// verbatim notes (part3-ux-spec §9.7) are used ONLY when the LLM call fails.
const PERSONAL_NOTES_FALLBACK: readonly string[] = [
  'hey — pulling you into our Cade workspace, this thing is starting to feel like an unfair advantage. curious what you think.',
  "adding you in. it's still rough in places but the moment it clicks you'll see it. flag anything weird.",
  'joining you in on this. should make our investigations a lot less painful.',
  "putting you on Cade. it's basically a cheat code for triage right now. tell me if i'm wrong.",
  "you should be in here. quick onboarding, then point it at whatever's been bugging you this week.",
  "adding you to Cade. it's the first AI tool we've tried that doesn't feel like a demo.",
  'this is the new SOC tool i mentioned — bringing you in. tell me what’s missing.',
];

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Human-friendly relative duration between `now` and a future ISO date. */
function relativeDuration(target: string | Date, now: Date = new Date()): string {
  const targetMs = new Date(target).getTime();
  const diffMs = targetMs - now.getTime();
  if (Number.isNaN(targetMs)) return '—';
  const abs = Math.abs(diffMs);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  let value: number;
  let unit: string;
  if (abs >= day) {
    value = Math.round(abs / day);
    unit = 'day';
  } else if (abs >= hour) {
    value = Math.round(abs / hour);
    unit = 'hour';
  } else {
    value = Math.max(1, Math.round(abs / minute));
    unit = 'minute';
  }
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

/** "in 6 days" / "Expired" for an invitation's `expiresAt`. */
function expiresInLabel(expiresAt: string | Date): string {
  const ms = new Date(expiresAt).getTime();
  if (Number.isNaN(ms)) return '—';
  if (ms <= Date.now()) return 'Expired';
  return `in ${relativeDuration(expiresAt)}`;
}

/**
 * "2 days ago" / "Never" for a past timestamp (last active, deactivated at).
 * `null`/missing renders `Never`; `< 1 minute` renders `Just now`.
 */
function pastLabel(value: string | Date | null | undefined): string {
  if (!value) return 'Never';
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'Just now';
  return `${relativeDuration(value)} ago`;
}

/** Up-to-two-letter initials for the avatar, from a name or email. */
function initialsFor(name: string | null | undefined, email: string): string {
  const source = name?.trim() || email;
  const parts = source.split(/[\s.@]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/** "SSO" for an IdP-backed user, "Local" otherwise. */
function authMethodLabel(authMode: string | null | undefined): string {
  return authMode === 'idp' ? 'SSO' : 'Local';
}

/** Picks a random note from the offline fallback set. */
function randomFallbackNote(): string {
  const idx = Math.floor(Math.random() * PERSONAL_NOTES_FALLBACK.length);
  return PERSONAL_NOTES_FALLBACK[idx] ?? '';
}

// Shapes returned by the auth router (loosely typed at the gateway boundary).
interface InvitationRow {
  id: string;
  email: string;
  isAdmin: boolean;
  workRoles: string[];
  personalMessage: string | null;
  status: string;
  expiresAt: string;
  resendCount: number;
  lastResentAt: string | null;
  createdAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  invitedByUserId: string | null;
}

interface UserRow {
  id: string;
  name: string | null;
  email: string;
  isAdmin: boolean;
  workRoles: string[];
  authMode: string | null;
  lastActiveAt: string | null;
  status: string;
  deactivatedAt: string | null;
}

/** Joined `workRoles` for display, or an em-dash when empty. */
function rolesLabel(workRoles: readonly string[] | undefined): string {
  const roles = (workRoles ?? []).filter(Boolean);
  return roles.length > 0 ? roles.join(', ') : '—';
}

// ── Component ───────────────────────────────────────────────────────────────

export function UserManagement() {
  const { isAdmin, token, userId } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('active');
  const [inviteOpen, setInviteOpen] = useState(false);

  // All hooks run unconditionally — fetches are gated via `enabled` rather than
  // by wrapping the hook call in a conditional (Rules of Hooks).
  const hasSession = !!token;
  const invitationsQuery = trpc.auth.listInvitations.useQuery(
    { sessionToken: token ?? '' },
    { enabled: hasSession && isAdmin },
  );
  const usersQuery = trpc.auth.listUsers.useQuery(
    { sessionToken: token ?? '' },
    { enabled: hasSession && isAdmin },
  );

  // Permission gate (Req 654): non-admins see only a centered placeholder.
  if (!isAdmin) {
    return (
      <div className="settings-placeholder">
        <p>You don&apos;t have access to user management.</p>
      </div>
    );
  }

  const invitations = (invitationsQuery.data ?? []) as unknown as InvitationRow[];
  const users = (usersQuery.data ?? []) as unknown as UserRow[];
  const pendingInvitations = invitations.filter((inv) => inv.status === 'pending');
  const activeUsers = users.filter((u) => u.status === 'active');
  const deactivatedUsers = users.filter((u) => u.status === 'deactivated');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 className="settings-section-title">Users</h2>
          <p className="settings-section-desc">
            Manage who has access to your Cade workspace, their roles, and pending invitations.
          </p>
        </div>
        <button className="btn-recommended" onClick={() => setInviteOpen(true)}>
          Invite teammate
        </button>
      </div>

      {/* Tab strip */}
      <div
        role="tablist"
        style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: 20 }}
      >
        {([
          { id: 'active' as TabId, label: 'Active' },
          { id: 'pending' as TabId, label: 'Pending invitations', count: pendingInvitations.length },
          { id: 'deactivated' as TabId, label: 'Deactivated' },
        ]).map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--color-accent)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              marginBottom: -1,
            }}
          >
            {tab.label}
            {typeof tab.count === 'number' && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  padding: '1px 6px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-bg-secondary)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'active' && (
        <ActiveUsers
          sessionToken={token ?? ''}
          users={activeUsers}
          currentUserId={userId}
          isLoading={usersQuery.isLoading}
          error={usersQuery.error?.message ?? null}
          onChanged={() => void usersQuery.refetch()}
        />
      )}

      {activeTab === 'deactivated' && (
        <DeactivatedUsers
          sessionToken={token ?? ''}
          users={deactivatedUsers}
          isLoading={usersQuery.isLoading}
          error={usersQuery.error?.message ?? null}
          onChanged={() => void usersQuery.refetch()}
        />
      )}

      {activeTab === 'pending' && (
        <PendingInvitations
          sessionToken={token ?? ''}
          invitations={pendingInvitations}
          users={users}
          isLoading={invitationsQuery.isLoading}
          error={invitationsQuery.error?.message ?? null}
          onChanged={() => void invitationsQuery.refetch()}
        />
      )}

      {inviteOpen && (
        <InviteModal
          sessionToken={token ?? ''}
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            setInviteOpen(false);
            void invitationsQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

// ── Pending invitations tab ─────────────────────────────────────────────────

function PendingInvitations({
  sessionToken,
  invitations,
  users,
  isLoading,
  error,
  onChanged,
}: {
  sessionToken: string;
  invitations: InvitationRow[];
  users: UserRow[];
  isLoading: boolean;
  error: string | null;
  onChanged: () => void;
}) {
  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      map.set(u.id, u.name?.trim() || u.email);
    }
    return map;
  }, [users]);

  if (isLoading) {
    return <div style={{ padding: 16, color: 'var(--color-text-muted)' }}>Loading…</div>;
  }
  if (error) {
    return <div style={{ padding: 16, color: 'var(--color-error)' }}>{error}</div>;
  }
  if (invitations.length === 0) {
    return (
      <div className="settings-placeholder">
        <p>No pending invitations.</p>
        <p className="settings-placeholder-hint">
          Invite a teammate to get them into your workspace.
        </p>
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
          <th style={thStyle}>Email</th>
          <th style={thStyle}>Role</th>
          <th style={thStyle}>Invited by</th>
          <th style={thStyle}>Expires in</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {invitations.map((inv) => (
          <PendingInvitationRow
            key={inv.id}
            sessionToken={sessionToken}
            invitation={inv}
            invitedByName={
              inv.invitedByUserId ? userNameById.get(inv.invitedByUserId) ?? '—' : '—'
            }
            onChanged={onChanged}
          />
        ))}
      </tbody>
    </table>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--color-border)',
  fontWeight: 600,
};
const tdStyle: React.CSSProperties = {
  padding: '12px',
  borderBottom: '1px solid var(--color-border)',
  verticalAlign: 'middle',
};

function PendingInvitationRow({
  sessionToken,
  invitation,
  invitedByName,
  onChanged,
}: {
  sessionToken: string;
  invitation: InvitationRow;
  invitedByName: string;
  onChanged: () => void;
}) {
  const resendMutation = trpc.auth.resendInvitation.useMutation();
  const revokeMutation = trpc.auth.revokeInvitation.useMutation();
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  // When rate-limited, the server returns a future `nextResendAt`.
  const [nextResendAt, setNextResendAt] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'sent' | 'suppressed' | 'failed' | null>(null);

  const rateLimited = !!nextResendAt && new Date(nextResendAt).getTime() > Date.now();

  async function handleResend() {
    setResendStatus(null);
    // Clear any prior cooldown so a fresh attempt's outcome (sent / failed /
    // a new cooldown) is the only status shown.
    setNextResendAt(null);
    try {
      const result = await resendMutation.mutateAsync({
        sessionToken,
        invitationId: invitation.id,
      });
      if (result.nextResendAt && new Date(result.nextResendAt).getTime() > Date.now()) {
        // Rate-limited: show the cooldown — no email was sent.
        setNextResendAt(result.nextResendAt);
      } else if (result.suppressed) {
        setResendStatus('suppressed');
      } else if (result.emailSent) {
        setResendStatus('sent');
      } else {
        setResendStatus('failed');
      }
      onChanged();
    } catch {
      // Mutation error surfaces below via resendMutation.error.
    }
  }

  async function handleRevoke() {
    try {
      await revokeMutation.mutateAsync({ sessionToken, invitationId: invitation.id });
      setConfirmingRevoke(false);
      onChanged();
    } catch {
      // Mutation error surfaces below via revokeMutation.error.
    }
  }

  const role = invitation.workRoles.length > 0 ? invitation.workRoles.join(', ') : '—';

  return (
    <tr>
      <td style={{ ...tdStyle, color: 'var(--color-text-primary)' }}>{invitation.email}</td>
      <td style={tdStyle}>{role}</td>
      <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>{invitedByName}</td>
      <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
        {expiresInLabel(invitation.expiresAt)}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {confirmingRevoke ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Revoke this invitation?</span>
            <button
              className="btn-danger"
              onClick={() => void handleRevoke()}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? 'Revoking…' : 'Confirm revoke'}
            </button>
            <button className="btn-secondary" onClick={() => setConfirmingRevoke(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            {rateLimited ? (
              <span style={{ color: 'var(--color-text-muted)' }}>
                Resend available in {relativeDuration(nextResendAt as string)}
              </span>
            ) : resendStatus === 'sent' ? (
              <span style={{ color: 'var(--color-success, #22c55e)' }}>Invitation resent</span>
            ) : resendStatus === 'suppressed' ? (
              <span style={{ color: 'var(--color-text-muted)' }}>
                On the suppression list — won&apos;t receive email
              </span>
            ) : resendStatus === 'failed' ? (
              <span style={{ color: 'var(--color-error)' }}>Couldn&apos;t send — try again</span>
            ) : null}
            <button
              className="btn-secondary"
              onClick={() => void handleResend()}
              disabled={resendMutation.isPending || rateLimited}
            >
              {resendMutation.isPending ? 'Resending…' : 'Resend'}
            </button>
            <button
              className="btn-secondary"
              style={{ color: 'var(--color-error)' }}
              onClick={() => setConfirmingRevoke(true)}
            >
              Revoke
            </button>
          </div>
        )}
        {resendMutation.error && !rateLimited && (
          <div style={{ marginTop: 4, color: 'var(--color-error)', fontSize: 12 }}>
            {resendMutation.error.message}
          </div>
        )}
        {revokeMutation.error && (
          <div style={{ marginTop: 4, color: 'var(--color-error)', fontSize: 12 }}>
            {revokeMutation.error.message}
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Shared cell pieces ──────────────────────────────────────────────────────

/** A subtle, non-loud tag (matches the mockup's neutral "You" badge style). */
function SubtleTag({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        marginLeft: 6,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '1px 6px',
        borderRadius: 'var(--radius-full)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
        color: 'var(--color-text-muted)',
      }}
    >
      {children}
    </span>
  );
}

/** Avatar + name + email cell, shared by the Active and Deactivated tabs. */
function UserCell({
  user,
  dimmed,
  isSelf,
}: {
  user: UserRow;
  dimmed?: boolean;
  isSelf?: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        aria-hidden
        style={{
          width: 30,
          height: 30,
          borderRadius: 'var(--radius-full)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 600,
          background: 'var(--color-bg-secondary)',
          color: 'var(--color-text-secondary)',
          border: '1px solid var(--color-border)',
          opacity: dimmed ? 0.6 : 1,
        }}
      >
        {initialsFor(user.name, user.email)}
      </div>
      <div>
        <div
          style={{
            color: dimmed ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
            fontWeight: 500,
          }}
        >
          {user.name?.trim() || user.email}
          {user.isAdmin && <SubtleTag>Admin</SubtleTag>}
          {isSelf && <SubtleTag>You</SubtleTag>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{user.email}</div>
      </div>
    </div>
  );
}

// ── Active tab (Req 655) ────────────────────────────────────────────────────

type SortKey = 'lastActive' | 'name' | 'role';

/** Compares two active-user rows for the chosen sort key (`asc` direction). */
function compareUsers(a: UserRow, b: UserRow, key: SortKey): number {
  if (key === 'name') {
    return (a.name?.trim() || a.email).localeCompare(b.name?.trim() || b.email);
  }
  if (key === 'role') {
    return rolesLabel(a.workRoles).localeCompare(rolesLabel(b.workRoles));
  }
  // lastActive — nulls sort last in ascending order.
  const aMs = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : -Infinity;
  const bMs = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : -Infinity;
  return aMs - bMs;
}

function ActiveUsers({
  sessionToken,
  users,
  currentUserId,
  isLoading,
  error,
  onChanged,
}: {
  sessionToken: string;
  users: UserRow[];
  currentUserId: string | null;
  isLoading: boolean;
  error: string | null;
  onChanged: () => void;
}) {
  // Default sort: Last active, descending (most-recent first).
  const [sortKey, setSortKey] = useState<SortKey>('lastActive');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Last active defaults to descending; the text columns default to ascending.
      setSortDir(key === 'lastActive' ? 'desc' : 'asc');
    }
  }

  const sortedUsers = useMemo(() => {
    const copy = [...users];
    copy.sort((a, b) => {
      const cmp = compareUsers(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [users, sortKey, sortDir]);

  if (isLoading) {
    return <div style={{ padding: 16, color: 'var(--color-text-muted)' }}>Loading…</div>;
  }
  if (error) {
    return <div style={{ padding: 16, color: 'var(--color-error)' }}>{error}</div>;
  }
  if (users.length === 0) {
    return (
      <div className="settings-placeholder">
        <p>No active users.</p>
        <p className="settings-placeholder-hint">Invite a teammate to get started.</p>
      </div>
    );
  }

  const sortIndicator = (key: SortKey) =>
    key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
          <SortableHeader
            label="Name"
            active={sortKey === 'name'}
            onClick={() => toggleSort('name')}
            indicator={sortIndicator('name')}
          />
          <SortableHeader
            label="Role"
            active={sortKey === 'role'}
            onClick={() => toggleSort('role')}
            indicator={sortIndicator('role')}
          />
          <SortableHeader
            label="Last active"
            active={sortKey === 'lastActive'}
            onClick={() => toggleSort('lastActive')}
            indicator={sortIndicator('lastActive')}
          />
          <th style={thStyle}>Auth method</th>
          <th style={{ ...thStyle, textAlign: 'right' }} aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {sortedUsers.map((user) => (
          <ActiveUserRow
            key={user.id}
            sessionToken={sessionToken}
            user={user}
            isSelf={user.id === currentUserId}
            onChanged={onChanged}
          />
        ))}
      </tbody>
    </table>
  );
}

function SortableHeader({
  label,
  active,
  onClick,
  indicator,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  indicator: string;
}) {
  return (
    <th style={thStyle}>
      <button
        type="button"
        onClick={onClick}
        aria-sort={active ? 'other' : 'none'}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          font: 'inherit',
          fontWeight: 600,
          cursor: 'pointer',
          color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        }}
      >
        {label}
        {indicator}
      </button>
    </th>
  );
}

function ActiveUserRow({
  sessionToken,
  user,
  isSelf,
  onChanged,
}: {
  sessionToken: string;
  user: UserRow;
  isSelf: boolean;
  onChanged: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const deactivateMutation = trpc.auth.deactivateUser.useMutation();

  // Close the ⋯ menu on an outside click.
  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  async function handleDeactivate() {
    try {
      await deactivateMutation.mutateAsync({ sessionToken, userId: user.id });
      setConfirmingDeactivate(false);
      onChanged();
    } catch {
      // Surfaced inline below via deactivateMutation.error (e.g. last-admin guard).
    }
  }

  return (
    <>
      <tr>
        <td style={tdStyle}>
          <UserCell user={user} isSelf={isSelf} />
        </td>
        <td style={{ ...tdStyle, color: 'var(--color-text-secondary)' }}>
          {rolesLabel(user.workRoles)}
        </td>
        <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
          {pastLabel(user.lastActiveAt)}
        </td>
        <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
          {authMethodLabel(user.authMode)}
        </td>
        <td style={{ ...tdStyle, textAlign: 'right', position: 'relative' }}>
          <div ref={menuRef} style={{ display: 'inline-block', position: 'relative' }}>
            <button
              className="btn-icon"
              aria-label="Row actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              ⋯
            </button>
            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: 4,
                  minWidth: 160,
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                  zIndex: 10,
                  padding: 4,
                  textAlign: 'left',
                }}
              >
                <RowMenuItem
                  label="Edit roles"
                  disabled={isSelf}
                  disabledReason="You can't edit your own access"
                  onClick={() => {
                    setMenuOpen(false);
                    setEditing(true);
                  }}
                />
                <RowMenuItem
                  label="Deactivate"
                  danger
                  disabled={isSelf}
                  disabledReason="You can't deactivate yourself"
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmingDeactivate(true);
                  }}
                />
              </div>
            )}
          </div>
        </td>
      </tr>

      {confirmingDeactivate && (
        <tr>
          <td colSpan={5} style={{ ...tdStyle, background: 'var(--color-bg-secondary)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ color: 'var(--color-text-primary)' }}>
                Deactivate <strong>{user.name?.trim() || user.email}</strong>? This signs them out
                and revokes their sessions. Their account stays on record and can be reactivated
                later.
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  className="btn-danger"
                  onClick={() => void handleDeactivate()}
                  disabled={deactivateMutation.isPending}
                >
                  {deactivateMutation.isPending ? 'Deactivating…' : 'Confirm deactivate'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setConfirmingDeactivate(false)}
                  disabled={deactivateMutation.isPending}
                >
                  Cancel
                </button>
              </div>
              {deactivateMutation.error && (
                <div style={{ color: 'var(--color-error)', fontSize: 12 }}>
                  {deactivateMutation.error.message}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}

      {editing && (
        <EditRolesRow
          sessionToken={sessionToken}
          user={user}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function RowMenuItem({
  label,
  onClick,
  danger,
  disabled,
  disabledReason,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '6px 10px',
        background: 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm, 4px)',
        fontSize: 13,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        color: danger ? 'var(--color-error)' : 'var(--color-text-primary)',
      }}
    >
      {label}
    </button>
  );
}

/** Inline edit-roles editor: a free-text roles input + an admin-access toggle. */
function EditRolesRow({
  sessionToken,
  user,
  onClose,
  onSaved,
}: {
  sessionToken: string;
  user: UserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [roleInput, setRoleInput] = useState((user.workRoles ?? []).join(', '));
  const [admin, setAdmin] = useState(user.isAdmin);
  const updateRolesMutation = trpc.auth.updateUserWorkRoles.useMutation();
  const setAdminMutation = trpc.auth.setAdminStatus.useMutation();

  const parsedRoles = roleInput
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean);
  const rolesChanged =
    JSON.stringify(parsedRoles) !== JSON.stringify((user.workRoles ?? []).filter(Boolean));
  const adminChanged = admin !== user.isAdmin;
  const saving = updateRolesMutation.isPending || setAdminMutation.isPending;
  const error = updateRolesMutation.error?.message ?? setAdminMutation.error?.message ?? null;

  async function handleSave() {
    try {
      if (rolesChanged) {
        await updateRolesMutation.mutateAsync({
          sessionToken,
          userId: user.id,
          workRoles: parsedRoles,
        });
      }
      // Admin status is changed last so a last-admin guard rejection doesn't
      // leave the work-roles update un-applied without feedback.
      if (adminChanged) {
        await setAdminMutation.mutateAsync({
          sessionToken,
          userId: user.id,
          isAdmin: admin,
        });
      }
      onSaved();
    } catch {
      // Surfaced inline below via the mutation error (e.g. last-admin guard).
    }
  }

  return (
    <tr>
      <td colSpan={5} style={{ ...tdStyle, background: 'var(--color-bg-secondary)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 460 }}>
          <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            Edit access — {user.name?.trim() || user.email}
          </div>
          <div className="form-field">
            <label htmlFor={`edit-roles-${user.id}`}>Roles</label>
            <input
              id={`edit-roles-${user.id}`}
              type="text"
              value={roleInput}
              onChange={(e) => setRoleInput(e.target.value)}
              placeholder="e.g. SOC Analyst, Threat Hunter"
            />
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              Free text, comma-separated. Cade interprets it to tailor their experience.
            </span>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={admin}
              onChange={(e) => setAdmin(e.target.checked)}
              style={{ width: 'auto' }}
            />
            Admin access
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn-primary"
              onClick={() => void handleSave()}
              disabled={saving || (!rolesChanged && !adminChanged)}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          </div>
          {error && (
            <div style={{ color: 'var(--color-error)', fontSize: 12 }}>{error}</div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Deactivated tab (mockup §X.7) ───────────────────────────────────────────

function DeactivatedUsers({
  sessionToken,
  users,
  isLoading,
  error,
  onChanged,
}: {
  sessionToken: string;
  users: UserRow[];
  isLoading: boolean;
  error: string | null;
  onChanged: () => void;
}) {
  if (isLoading) {
    return <div style={{ padding: 16, color: 'var(--color-text-muted)' }}>Loading…</div>;
  }
  if (error) {
    return <div style={{ padding: 16, color: 'var(--color-error)' }}>{error}</div>;
  }
  if (users.length === 0) {
    return (
      <div className="settings-placeholder">
        <p>No deactivated users.</p>
        <p className="settings-placeholder-hint">
          Deactivated teammates appear here and can be reactivated.
        </p>
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
          <th style={thStyle}>Name</th>
          <th style={thStyle}>Role</th>
          <th style={thStyle}>Deactivated</th>
          <th style={thStyle}>By</th>
          <th style={{ ...thStyle, textAlign: 'right' }} aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {users.map((user) => (
          <DeactivatedUserRow
            key={user.id}
            sessionToken={sessionToken}
            user={user}
            onChanged={onChanged}
          />
        ))}
      </tbody>
    </table>
  );
}

function DeactivatedUserRow({
  sessionToken,
  user,
  onChanged,
}: {
  sessionToken: string;
  user: UserRow;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const reactivateMutation = trpc.auth.reactivateUser.useMutation();

  async function handleReactivate() {
    try {
      await reactivateMutation.mutateAsync({ sessionToken, userId: user.id });
      setConfirming(false);
      onChanged();
    } catch {
      // Surfaced inline below via reactivateMutation.error.
    }
  }

  return (
    <tr>
      <td style={tdStyle}>
        <UserCell user={user} dimmed />
      </td>
      <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
        {rolesLabel(user.workRoles)}
      </td>
      <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
        {pastLabel(user.deactivatedAt)}
      </td>
      {/* `listUsers` rows don't carry `deactivatedByUserId` — show "—". */}
      <td style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>—</td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {confirming ? (
          <div
            style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}
          >
            <span style={{ color: 'var(--color-text-secondary)' }}>
              Reactivate this user?
            </span>
            <button
              className="btn-primary"
              onClick={() => void handleReactivate()}
              disabled={reactivateMutation.isPending}
            >
              {reactivateMutation.isPending ? 'Reactivating…' : 'Confirm'}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setConfirming(false)}
              disabled={reactivateMutation.isPending}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button className="btn-secondary" onClick={() => setConfirming(true)}>
            Reactivate
          </button>
        )}
        {reactivateMutation.error && (
          <div style={{ marginTop: 4, color: 'var(--color-error)', fontSize: 12 }}>
            {reactivateMutation.error.message}
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Invite modal ────────────────────────────────────────────────────────────

function InviteModal({
  sessionToken,
  onClose,
  onInvited,
}: {
  sessionToken: string;
  onClose: () => void;
  onInvited: () => void;
}) {
  const createMutation = trpc.auth.createInvitation.useMutation();
  // Every personal note is LLM-generated (Req 657) — fresh on open and on each
  // "Suggest another". The static PERSONAL_NOTES_FALLBACK set is used only when
  // this call fails.
  const suggestNoteMutation = trpc.aiOrchestrator.suggestInviteNote.useMutation();
  const [email, setEmail] = useState('');
  const [roleInput, setRoleInput] = useState('');
  // Personal note: `noteOpen === false` means the note is collapsed (button
  // state). When open, the textarea is shown with `noteText` (LLM-generated,
  // user-editable). `noteLoading` is true while a generation call is in flight.
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  // Set when the invitation row was created but its email did not go out
  // (provider failure or suppressed recipient). The modal stays open with this
  // notice instead of closing on success.
  const [emailWarning, setEmailWarning] = useState<string | null>(null);

  /** Generate a fresh note via the LLM; fall back to a static note on error. */
  async function generateNote() {
    setNoteLoading(true);
    try {
      const result = await suggestNoteMutation.mutateAsync({});
      setNoteText(result.note);
    } catch {
      // LLM call failed (offline/error) — fall back to a random static note.
      setNoteText(randomFallbackNote());
    } finally {
      setNoteLoading(false);
    }
  }

  /** "Add a personal note" — opens the textarea and generates the first note. */
  async function openNote() {
    setNoteOpen(true);
    setNoteText('');
    await generateNote();
  }

  /** "Suggest another" — generates a fresh note in place. */
  async function shuffleNote() {
    await generateNote();
  }

  function removeNote() {
    setNoteOpen(false);
    setNoteText('');
    setNoteLoading(false);
  }

  async function handleSubmit() {
    const workRoles = roleInput
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
    const personalMessage = noteOpen && noteText.trim() ? noteText.trim() : undefined;
    setEmailWarning(null);
    try {
      const result = await createMutation.mutateAsync({
        sessionToken,
        email: email.trim(),
        isAdmin: false, // Req 657: the invite modal has no admin toggle.
        workRoles,
        ...(personalMessage ? { personalMessage } : {}),
      });
      if (result.emailSent) {
        onInvited();
      } else {
        // The invitation row was created, but its email did not go out.
        // Re-submitting would hit a "pending invitation already exists"
        // conflict, so keep the modal open and point the admin at Resend.
        setEmailWarning(
          result.suppressed
            ? 'Invitation created, but this address is on the suppression list and won’t receive email.'
            : 'Invitation created, but the email could not be sent. Close this and use Resend on the Pending invitations tab.',
        );
      }
    } catch {
      // Error (incl. off-domain) surfaces inline below; modal stays open.
    }
  }

  const canSubmit = email.trim().length > 0 && !createMutation.isPending;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-content" role="dialog" aria-modal="true" aria-label="Invite a teammate">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h3 className="modal-title">Invite a teammate</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 4 }}>
              They&apos;ll get an email with a single-use link to join.
            </p>
          </div>
          <button className="btn-icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="form-field">
          <label htmlFor="invite-email">
            Email <span style={{ color: 'var(--color-error)' }}>*</span>
          </label>
          <input
            id="invite-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
          />
        </div>

        <div className="form-field">
          <label htmlFor="invite-role">Role (optional)</label>
          <input
            id="invite-role"
            type="text"
            value={roleInput}
            onChange={(e) => setRoleInput(e.target.value)}
            placeholder="What do they focus on? e.g. SOC Analyst, Threat Hunter"
          />
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            Free text. Add multiple if you like, Cade interprets it to tailor their experience.
          </span>
        </div>

        {/* Personal note — dashed-border button that expands into a textarea. */}
        <div>
          {!noteOpen ? (
            <button
              type="button"
              onClick={() => void openNote()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: 'transparent',
                border: '1px dashed var(--color-border-strong)',
                borderRadius: 'var(--radius)',
                color: 'var(--color-text-secondary)',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              + Add a personal note
            </button>
          ) : (
            <div className="form-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label htmlFor="invite-note" style={{ margin: 0 }}>
                  Personal note
                </label>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => void shuffleNote()}
                  disabled={noteLoading}
                >
                  {noteLoading ? 'Thinking…' : 'Suggest another'}
                </button>
              </div>
              <textarea
                id="invite-note"
                rows={3}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                disabled={noteLoading}
                placeholder={
                  noteLoading ? '✨ thinking of something good…' : 'Write a short note…'
                }
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 13,
                  padding: '8px 10px',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text-primary)',
                  resize: 'vertical',
                  opacity: noteLoading ? 0.6 : 1,
                  transition: 'opacity 120ms ease',
                }}
              />
              <button
                type="button"
                onClick={removeNote}
                style={{
                  alignSelf: 'flex-start',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  fontSize: 12,
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                Remove note
              </button>
            </div>
          )}
        </div>

        {createMutation.error && (
          <div style={{ color: 'var(--color-error)', fontSize: 13 }}>
            {createMutation.error.message}
          </div>
        )}

        {emailWarning && (
          <div style={{ color: 'var(--color-warning, #b45309)', fontSize: 13 }}>{emailWarning}</div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {createMutation.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
